import { generateAccessCode, REACTION_TYPES } from "@/lib/eventConfig";
import { PLAN_LIMITS, getMaxPhotosPerParticipant, getPhotoExpirationDate } from "@/lib/planLimits";
import { runtimeConfig } from "@/lib/runtimeConfig";
import { supabase } from "@/lib/supabase";
import { getDonationCapCents } from "@/lib/donationCaps";
import { hasPlanFeature } from "@/lib/planFeatures";
import { calculateProcessingFeeCents } from "@/lib/paymentFees";
import { rankMoments } from "@/lib/moments";

const LOCAL_DB_KEY = "souvenir_local_db_v2";
const LOCAL_USER_KEY = "souvenir_local_user_v1";
const AUTH_EVENT = "souvenir:auth-changed";

const ENTITY_TABLES = {
  Event: "events",
  Photo: "photos",
  EventParticipant: "event_participants",
  Reaction: "reactions",
  Notification: "notifications",
  GuestToken: "guest_tokens",
  GiftFund: "gift_funds",
  Donation: "donations",
  EventCheckout: "event_checkouts",
  OrganizerPayoutAccount: "organizer_payout_accounts",
  SupportTicket: "support_tickets",
  SupportTicketMessage: "support_ticket_messages",
  GuestAccessSession: "guest_access_sessions",
  PhotoFavorite: "photo_favorites",
  EventPhotoPick: "event_photo_picks",
  PhotoMission: "photo_missions",
  PhotoMissionSubmission: "photo_mission_submissions",
  PhotoReport: "photo_reports",
};

const entitySubscribers = new Map();

function nowIso() {
  return new Date().toISOString();
}

function clone(value) {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

function isApprovedVisiblePhoto(photo) {
  return Boolean(photo?.is_visible) && String(photo?.moderation_status || "").toLowerCase() === "approved";
}

function cleanObject(input) {
  return Object.fromEntries(
    Object.entries(input || {}).filter(([, value]) => value !== undefined)
  );
}

function defaultCollections() {
  return {
    events: [],
    photos: [],
    event_participants: [],
    reactions: [],
    notifications: [],
    guest_tokens: [],
    gift_funds: [],
    donations: [],
    event_checkouts: [],
    organizer_payout_accounts: [],
    support_tickets: [],
    support_ticket_messages: [],
    guest_access_sessions: [],
    photo_favorites: [],
    event_photo_picks: [],
    photo_missions: [],
    photo_mission_submissions: [],
    photo_reports: [],
    photo_view_sessions: [],
    audit_logs: [],
  };
}

function ensureLocalDb() {
  if (typeof window === "undefined") return defaultCollections();

  try {
    const parsed = JSON.parse(window.localStorage.getItem(LOCAL_DB_KEY) || "{}");
    const db = { ...defaultCollections(), ...parsed };
    Object.keys(defaultCollections()).forEach((key) => {
      if (!Array.isArray(db[key])) db[key] = [];
    });
    return db;
  } catch {
    return defaultCollections();
  }
}

function saveLocalDb(db) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_DB_KEY, JSON.stringify(db));
}

function readLocalUser() {
  if (typeof window === "undefined") return null;

  try {
    return JSON.parse(window.localStorage.getItem(LOCAL_USER_KEY) || "null");
  } catch {
    return null;
  }
}

function writeLocalUser(user) {
  if (typeof window === "undefined") return;
  if (user) {
    window.localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(user));
  } else {
    window.localStorage.removeItem(LOCAL_USER_KEY);
  }
  window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: user || null }));
}

function tableFor(entityName) {
  const table = ENTITY_TABLES[entityName];
  if (!table) throw new Error(`Unknown entity: ${entityName}`);
  return table;
}

function matchesFilters(record, filters = {}) {
  return Object.entries(filters).every(([key, value]) => {
    if (value === undefined) return true;
    if (value === null) return record[key] == null;
    return record[key] === value;
  });
}

function sortRecords(records, sort) {
  if (!sort) return records;

  const descending = String(sort).startsWith("-");
  const field = descending ? String(sort).slice(1) : String(sort);
  const direction = descending ? -1 : 1;

  return [...records].sort((left, right) => {
    const a = left?.[field];
    const b = right?.[field];

    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    const aDate = typeof a === "string" ? Date.parse(a) : NaN;
    const bDate = typeof b === "string" ? Date.parse(b) : NaN;

    if (!Number.isNaN(aDate) && !Number.isNaN(bDate)) {
      return (aDate - bDate) * direction;
    }

    if (a > b) return 1 * direction;
    if (a < b) return -1 * direction;
    return 0;
  });
}

function applyLimit(records, limit) {
  return typeof limit === "number" ? records.slice(0, limit) : records;
}

function subscribeLocalEntity(entityName, callback) {
  if (!entitySubscribers.has(entityName)) {
    entitySubscribers.set(entityName, new Set());
  }
  entitySubscribers.get(entityName).add(callback);

  return () => {
    entitySubscribers.get(entityName)?.delete(callback);
  };
}

function emitEntityEvent(entityName, type, data, fallbackId) {
  const payload = {
    entity: entityName,
    type,
    data: data ? clone(data) : null,
    id: data?.id || fallbackId || null,
  };

  entitySubscribers.get(entityName)?.forEach((callback) => callback(payload));
}

function createSecureToken(length = 24) {
  const source = crypto.randomUUID().replace(/-/g, "");
  if (source.length >= length) return source.slice(0, length);
  return `${source}${crypto.randomUUID().replace(/-/g, "")}`.slice(0, length);
}

function sanitizeFileName(fileName = "upload") {
  return fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
}

function sanitizeRequestedPlan(plan) {
  const requestedPlan = String(plan || "free").toLowerCase();
  return runtimeConfig.enabledPlans.includes(requestedPlan) ? requestedPlan : "free";
}

function sanitizeKnownPlan(plan) {
  const requestedPlan = String(plan || "").toLowerCase();
  return Object.prototype.hasOwnProperty.call(PLAN_LIMITS, requestedPlan) ? requestedPlan : "";
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isRuntimeAdminEmail(email) {
  return (runtimeConfig.adminEmails || []).includes(normalizeEmail(email));
}

function resolveUserRole(user) {
  const role = String(user?.app_metadata?.role || user?.role || "").toLowerCase();
  if (role === "admin" || isRuntimeAdminEmail(user?.email)) return "admin";
  return role || "member";
}

function pickEntitledPlan(requestedPlan, user) {
  const planRank = { free: 0, standard: 1, plus: 2, premium: 3 };
  const requested = sanitizeRequestedPlan(requestedPlan);
  const manualPlan = sanitizeKnownPlan(user?.plan);
  if (manualPlan && (planRank[manualPlan] || 0) > (planRank[requested] || 0)) {
    return manualPlan;
  }
  return requested;
}

function sanitizeEventOptions(options = []) {
  const enabledOptions = new Set();
  if (runtimeConfig.giftFundEnabled) enabledOptions.add("gift_fund");

  return (options || []).filter((option) => enabledOptions.has(option));
}

function eventHasOption(event, option) {
  const options = event?.options;
  if (Array.isArray(options)) return options.includes(option);
  if (typeof options === "string") {
    try {
      const parsed = JSON.parse(options);
      if (Array.isArray(parsed)) return parsed.includes(option);
    } catch {
      return options.includes(option);
    }
  }
  return false;
}

function toDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });
}

function normalizeSupabaseUser(user) {
  if (!user) return null;

  return {
    id: user.id,
    email: user.email || "",
    full_name:
      user.user_metadata?.full_name ||
      user.user_metadata?.name ||
      user.email?.split("@")[0] ||
      "Member",
    avatar_url: user.user_metadata?.avatar_url || "",
    role: resolveUserRole(user),
    plan: sanitizeKnownPlan(user.app_metadata?.plan) || "free",
    plan_updated_at: user.app_metadata?.plan_updated_at || null,
    plan_updated_by: user.app_metadata?.plan_updated_by || "",
  };
}

async function currentUser() {
  if (!supabase) return readLocalUser();

  const { data, error } = await supabase.auth.getUser();
  if (error) return null;
  return normalizeSupabaseUser(data.user);
}

async function signPhotoRows(records) {
  if (!supabase || !Array.isArray(records) || records.length === 0) return records;
  const paths = Array.from(new Set(
    records.map((record) => String(record?.storage_path || "").trim()).filter(Boolean)
  ));
  if (!paths.length) return records;

  const { data, error } = await supabase.storage
    .from(runtimeConfig.supabaseStorageBucket)
    .createSignedUrls(paths, 3600);
  if (error || !data) return records;

  const signedUrlByPath = new Map(
    data
      .filter((item) => item.path && item.signedUrl)
      .map((item) => [String(item.path), String(item.signedUrl)])
  );

  const feedUrlByPath = new Map();
  if (runtimeConfig.imageTransformsEnabled) {
    const transformPaths = paths.slice(0, 40);
    const concurrency = 8;
    for (let index = 0; index < transformPaths.length; index += concurrency) {
      const batch = transformPaths.slice(index, index + concurrency);
      const transformed = await Promise.all(batch.map(async (path) => {
        const { data: transformedData, error: transformedError } = await supabase.storage
          .from(runtimeConfig.supabaseStorageBucket)
          .createSignedUrl(path, 3600, {
            transform: { width: 1280, quality: 80, resize: "contain" },
          });
        return transformedError ? null : [path, transformedData?.signedUrl || ""];
      }));
      transformed.filter(Boolean).forEach(([path, url]) => {
        if (url) feedUrlByPath.set(path, url);
      });
    }
  }

  return records.map((record) => {
    const storagePath = String(record?.storage_path || "").trim();
    const signedUrl = signedUrlByPath.get(storagePath);
    return signedUrl
      ? { ...record, file_url: signedUrl, feed_url: feedUrlByPath.get(storagePath) || signedUrl }
      : record;
  });
}

async function readRecords(entityName, { filters = {}, sort, limit } = {}) {
  const table = tableFor(entityName);

  if (!supabase) {
    const db = ensureLocalDb();
    const records = db[table].filter((record) => matchesFilters(record, filters));
    return clone(applyLimit(sortRecords(records, sort), limit));
  }

  let query = supabase.from(table).select("*");

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined) return;
    if (value === null) query = query.is(key, null);
    else query = query.eq(key, value);
  });

  if (sort) {
    const descending = String(sort).startsWith("-");
    const field = descending ? String(sort).slice(1) : String(sort);
    query = query.order(field, { ascending: !descending });
  }

  if (typeof limit === "number") {
    query = query.limit(limit);
  }

  const { data, error } = await query;
  if (error) throw error;
  const rows = data || [];
  return entityName === "Photo" ? signPhotoRows(rows) : rows;
}

async function readRecordById(entityName, id) {
  const table = tableFor(entityName);

  if (!supabase) {
    const db = ensureLocalDb();
    const record = db[table].find((item) => item.id === id);
    return record ? clone(record) : null;
  }

  const { data, error } = await supabase.from(table).select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  if (!data) return null;
  if (entityName !== "Photo") return data;
  const [signedPhoto] = await signPhotoRows([data]);
  return signedPhoto || data;
}

function prepareRecord(entityName, payload, user) {
  const timestamp = nowIso();
  const base = {
    id: payload.id || crypto.randomUUID(),
    created_date: payload.created_date || payload.created_at || timestamp,
    updated_date: timestamp,
    created_at: payload.created_at || payload.created_date || timestamp,
    updated_at: timestamp,
    ...payload,
  };

  if (entityName === "GuestToken") {
    base.token = payload.token || createSecureToken();
    base.is_active = payload.is_active ?? true;
    base.view_count = payload.view_count ?? 0;
  }

  if (entityName === "Notification") {
    base.is_read = payload.is_read ?? false;
  }

  if (entityName === "Photo") {
    base.heart_count = payload.heart_count ?? 0;
    base.funny_count = payload.funny_count ?? 0;
    base.shocked_count = payload.shocked_count ?? 0;
    base.view_count = payload.view_count ?? 0;
    base.favorite_count = payload.favorite_count ?? 0;
    base.share_count = payload.share_count ?? 0;
    base.engagement_score = payload.engagement_score ?? 0;
    base.is_visible = payload.is_visible ?? true;
    base.moderation_status = payload.moderation_status || (base.is_visible ? "approved" : "pending_review");
    base.moderation_flags = payload.moderation_flags ?? [];
    base.uploader_email = payload.uploader_email || user?.email || "";
    base.uploader_name = payload.uploader_name || user?.full_name || user?.email || "Guest";
  }

  if (entityName === "EventParticipant") {
    base.photo_count = payload.photo_count ?? 0;
    base.role = payload.role || "participant";
    base.display_name = payload.display_name || user?.full_name || payload.user_email || "Participant";
  }

  if (entityName === "Donation") {
    base.status = payload.status || "completed";
    base.currency = payload.currency || "USD";
    base.stripe_transfer_status = payload.stripe_transfer_status || "not_configured";
  }

  if (entityName === "GiftFund") {
    base.total_amount = payload.total_amount ?? 0;
    base.donation_count = payload.donation_count ?? 0;
    base.status = payload.status || "active";
    base.currency = payload.currency || "USD";
  }

  if (entityName === "SupportTicket") {
    base.user_email = payload.user_email || user?.email || "";
    base.user_id = payload.user_id || user?.id || null;
    base.user_name = payload.user_name || user?.full_name || user?.email || "";
    base.category = payload.category || "support";
    base.priority = payload.priority || "normal";
    base.status = payload.status || "open";
    base.last_message_at = payload.last_message_at || timestamp;
  }

  if (entityName === "SupportTicketMessage") {
    base.sender_email = payload.sender_email || user?.email || "";
    base.sender_name = payload.sender_name || user?.full_name || user?.email || "";
    base.sender_role = payload.sender_role || "customer";
    base.is_internal = payload.is_internal ?? false;
  }

  if (entityName === "PhotoMission") {
    base.status = payload.status || "active";
    base.sort_order = payload.sort_order ?? 0;
    base.created_by = payload.created_by || user?.email || "";
  }

  if (entityName === "EventPhotoPick") {
    base.sort_order = payload.sort_order ?? 0;
    base.note = payload.note || "";
    base.picked_by = payload.picked_by || user?.email || "";
  }

  return cleanObject(base);
}

async function createRecord(entityName, payload, options = {}) {
  const table = tableFor(entityName);
  const user = options.user || (await currentUser());
  const record = prepareRecord(entityName, payload, user);

  if (!supabase) {
    const db = ensureLocalDb();
    db[table].push(record);
    saveLocalDb(db);
    await runHooks(entityName, "create", record, null);
    emitEntityEvent(entityName, "create", record);
    return clone(record);
  }

  const { data, error } = await supabase.from(table).insert(record).select("*").single();
  if (error) throw error;
  await runHooks(entityName, "create", data, null);
  return data;
}

async function updateRecord(entityName, id, changes, options = {}) {
  const table = tableFor(entityName);
  const current = await readRecordById(entityName, id);
  if (!current) throw new Error(`${entityName} not found`);

  const next = cleanObject({
    ...changes,
    updated_date: nowIso(),
    updated_at: nowIso(),
  });

  if (!supabase) {
    const db = ensureLocalDb();
    const index = db[table].findIndex((item) => item.id === id);
    db[table][index] = { ...db[table][index], ...next };
    saveLocalDb(db);
    const updated = clone(db[table][index]);
    await runHooks(entityName, "update", updated, current);
    emitEntityEvent(entityName, "update", updated);
    return updated;
  }

  const { data, error } = await supabase.from(table).update(next).eq("id", id).select("*").single();
  if (error) throw error;
  await runHooks(entityName, "update", data, current);
  return data;
}

async function deleteRecord(entityName, id) {
  const table = tableFor(entityName);
  const current = await readRecordById(entityName, id);
  if (!current) return true;

  if (!supabase) {
    const db = ensureLocalDb();
    db[table] = db[table].filter((item) => item.id !== id);
    saveLocalDb(db);
    await runHooks(entityName, "delete", null, current);
    emitEntityEvent(entityName, "delete", null, id);
    return true;
  }

  const { error } = await supabase.from(table).delete().eq("id", id);
  if (error) throw error;
  await runHooks(entityName, "delete", null, current);
  return true;
}

async function ensureParticipant(event, user, role = "participant") {
  if (!user?.email || !event?.id) return null;

  const existing = await readRecords("EventParticipant", {
    filters: { event_id: event.id, user_email: user.email },
    limit: 1,
  });

  if (existing[0]) return existing[0];

  return createRecord("EventParticipant", {
    event_id: event.id,
    user_email: user.email,
    display_name: user.full_name || user.email,
    role,
  });
}

async function syncEventCounts(eventId) {
  const event = await readRecordById("Event", eventId);
  if (!event) return null;

  const participants = await readRecords("EventParticipant", { filters: { event_id: eventId } });
  const photos = await readRecords("Photo", { filters: { event_id: eventId } });

  return updateRecord("Event", eventId, {
    participant_count: participants.length,
    photo_count: photos.length,
  });
}

async function syncParticipantPhotoCount(eventId, userEmail) {
  if (!eventId || !userEmail) return null;

  const [participant] = await readRecords("EventParticipant", {
    filters: { event_id: eventId, user_email: userEmail },
    limit: 1,
  });

  if (!participant) return null;

  const photos = await readRecords("Photo", { filters: { event_id: eventId, uploader_email: userEmail } });
  return updateRecord("EventParticipant", participant.id, { photo_count: photos.length });
}

async function syncGiftFundTotals(eventId, giftFundId) {
  const [giftFund] = giftFundId
    ? [await readRecordById("GiftFund", giftFundId)]
    : await readRecords("GiftFund", { filters: { event_id: eventId }, limit: 1 });

  if (!giftFund) return null;

  const donations = await readRecords("Donation", {
    filters: { event_id: giftFund.event_id, status: "completed" },
  });

  const totalAmount = donations.reduce((sum, donation) => sum + Number(donation.amount || 0), 0);

  return updateRecord("GiftFund", giftFund.id, {
    total_amount: totalAmount,
    donation_count: donations.length,
  });
}

async function removeStoredAsset(record) {
  if (!supabase) return;

  const bucket = runtimeConfig.supabaseStorageBucket;
  const publicPrefix = `/storage/v1/object/public/${bucket}/`;
  const signedPrefix = `/storage/v1/object/sign/${bucket}/`;
  const fileUrl = record?.file_url || "";
  let storagePath = record?.storage_path || "";

  if (!storagePath && fileUrl.includes(publicPrefix)) {
    storagePath = decodeURIComponent(fileUrl.split(publicPrefix)[1] || "");
  }

  if (!storagePath && fileUrl.includes(signedPrefix)) {
    storagePath = decodeURIComponent(fileUrl.split(signedPrefix)[1]?.split("?")[0] || "");
  }

  if (!storagePath) return;
  await supabase.storage.from(bucket).remove([storagePath]);
}

async function createNotification(payload) {
  try {
    return await createRecord("Notification", payload);
  } catch {
    return null;
  }
}

async function runHooks(entityName, action, next, previous) {
  if (entityName === "EventParticipant") {
    const eventId = next?.event_id || previous?.event_id;
    await syncEventCounts(eventId);
  }

  if (entityName === "Photo") {
    const eventId = next?.event_id || previous?.event_id;
    const userEmail = next?.uploader_email || previous?.uploader_email;
    await syncEventCounts(eventId);
    await syncParticipantPhotoCount(eventId, userEmail);

    if (action === "delete" && previous) {
      await removeStoredAsset(previous);
      const reactions = await readRecords("Reaction", { filters: { photo_id: previous.id } });
      await Promise.all(reactions.map((reaction) => deleteRecord("Reaction", reaction.id)));
      if (!supabase) {
        const relatedEntities = ["PhotoFavorite", "EventPhotoPick", "PhotoMissionSubmission", "PhotoReport"];
        for (const relatedEntity of relatedEntities) {
          const records = await readRecords(relatedEntity, { filters: { photo_id: previous.id } });
          await Promise.all(records.map((record) => deleteRecord(relatedEntity, record.id)));
        }
        const db = ensureLocalDb();
        db.photo_view_sessions = db.photo_view_sessions.filter((view) => view.photo_id !== previous.id);
        saveLocalDb(db);
      }
    }

    if (
      action === "update" &&
      next?.event_id &&
      isApprovedVisiblePhoto(next) &&
      !isApprovedVisiblePhoto(previous)
    ) {
      const event = await readRecordById("Event", next.event_id);
      if (event?.organizer_email && next.uploader_email !== event.organizer_email) {
        await createNotification({
          user_email: event.organizer_email,
          event_id: event.id,
          event_title: event.title,
          type: "new_photo",
          actor_name: next.uploader_name || next.uploader_email,
        });
      }
    }
  }

  if (entityName === "Donation") {
    await syncGiftFundTotals(next?.event_id || previous?.event_id, next?.gift_fund_id || previous?.gift_fund_id);
  }

  if (entityName === "Reaction" && action === "create" && next?.photo_id) {
    const photo = await readRecordById("Photo", next.photo_id);
    if (photo?.uploader_email && photo.uploader_email !== next.user_email) {
      const event = await readRecordById("Event", photo.event_id);
      await createNotification({
        user_email: photo.uploader_email,
        event_id: photo.event_id,
        event_title: event?.title || "",
        type: "reaction",
        actor_name: next.user_email,
        reaction_type: next.type,
      });
    }
  }
}

async function generateUniqueAccessCode() {
  for (let attempt = 0; attempt < 5; attempt += 1) {
    const code = generateAccessCode();
    const existing = await readRecords("Event", { filters: { access_code: code }, limit: 1 });
    if (!existing.length) return code;
  }
  return generateAccessCode();
}

function sanitizeGuestTokenSummary(guestToken) {
  if (!guestToken) return null;

  return {
    id: guestToken.id,
    label: guestToken.label || "",
    expires_at: guestToken.expires_at || null,
    view_count: Number(guestToken.view_count || 0),
  };
}

function sanitizeGuestEvent(event) {
  if (!event) return null;

  return {
    id: event.id,
    title: event.title,
    event_type: event.event_type,
    cover_image_url: event.cover_image_url || "",
    date: event.date || null,
    end_date: event.end_date || null,
    location: event.location || "",
    participant_count: Number(event.participant_count || 0),
    photo_count: Number(event.photo_count || 0),
    status: event.status,
    plan: event.plan || "free",
    max_participants: Number(event.max_participants || 0),
    max_photos_per_participant: Number(event.max_photos_per_participant || 0),
    access_code: event.access_code || "",
  };
}

function sanitizeGuestPhoto(photo) {
  if (!photo) return null;

  return {
    id: photo.id,
    file_url: photo.file_url,
    feed_url: photo.feed_url || photo.file_url,
    caption: photo.caption || "",
    uploader_email: photo.uploader_email || "",
    uploader_name: photo.uploader_name || "",
    heart_count: Number(photo.heart_count || 0),
    funny_count: Number(photo.funny_count || 0),
    shocked_count: Number(photo.shocked_count || 0),
    view_count: Number(photo.view_count || 0),
    favorite_count: Number(photo.favorite_count || 0),
    share_count: Number(photo.share_count || 0),
    engagement_score: Number(photo.engagement_score || 0),
    is_visible: photo.is_visible !== false,
    moderation_status: photo.moderation_status || "approved",
    created_date: photo.created_date || photo.created_at || null,
  };
}

function getGuestAccessExpiry(event) {
  const dateValue = String(event?.end_date || event?.date || "").trim();
  if (!dateValue) {
    return new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  }

  const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 23, 59, 59, 999)).toISOString();
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
  }
  parsed.setUTCHours(23, 59, 59, 999);
  return parsed.toISOString();
}

function normalizeGuestEmail(value) {
  return normalizeEmail(value);
}

async function createLocalGuestAccessSession({ event, participant, guestEmail, displayName }) {
  const token = createSecureToken(48);
  const expiresAt = getGuestAccessExpiry(event);
  const existing = (await readRecords("GuestAccessSession", {
    filters: { event_id: event.id, guest_email: guestEmail },
    limit: 1,
  }))[0];

  const payload = {
    event_id: event.id,
    participant_id: participant?.id || null,
    guest_email: guestEmail,
    display_name: displayName || participant?.display_name || guestEmail.split("@")[0],
    token,
    expires_at: expiresAt,
    revoked_at: null,
    last_seen_at: nowIso(),
  };

  if (existing) {
    await updateRecord("GuestAccessSession", existing.id, payload);
    return { ...existing, ...payload, id: existing.id };
  }

  return createRecord("GuestAccessSession", payload);
}

async function resolveLocalGuestAccessSession(tokenValue, expectedEventId = "") {
  const token = String(tokenValue || "").trim();
  if (!token) return { error: "Guest access required", verification_required: true };

  const [session] = await readRecords("GuestAccessSession", {
    filters: { token },
    limit: 1,
  });

  if (!session) return { error: "Guest access invalid" };
  if (expectedEventId && session.event_id !== expectedEventId) {
    return { error: "Guest access does not match this event" };
  }
  if (session.revoked_at) return { error: "Guest access revoked" };
  if (new Date(session.expires_at) <= new Date()) {
    return { error: "Verification required", verification_required: true };
  }

  const event = await readRecordById("Event", session.event_id);
  if (!event) return { error: "Event not found" };
  if (!["active", "archived"].includes(event.status)) return { error: "Event not active" };

  return { session, event, guestEmail: normalizeGuestEmail(session.guest_email) };
}

async function resolveGuestViewFromDataStore(tokenValue, { incrementViewCount = true } = {}) {
  const [guestToken] = await readRecords("GuestToken", {
    filters: { token: tokenValue },
    limit: 1,
  });

  if (!guestToken) {
    return { error: "Lien invalide ou expire." };
  }

  if (guestToken.expires_at && new Date(guestToken.expires_at) < new Date()) {
    return { error: "Ce lien a expire." };
  }

  if (!guestToken.is_active) {
    return { error: "Ce lien a ete desactive par l'organisateur." };
  }

  const event = await readRecordById("Event", guestToken.event_id);
  if (!event) {
    return { error: "Evenement introuvable." };
  }

  if (!["active", "archived"].includes(event.status)) {
    return { error: "Evenement indisponible." };
  }

  if (incrementViewCount) {
    try {
      await updateRecord("GuestToken", guestToken.id, {
        view_count: Number(guestToken.view_count || 0) + 1,
      });
      guestToken.view_count = Number(guestToken.view_count || 0) + 1;
    } catch {
      // A failed analytics-style counter increment should not block gallery access.
    }
  }

  const photos = await readRecords("Photo", {
    filters: { event_id: event.id, is_visible: true, moderation_status: "approved" },
    sort: "-created_date",
  });
  const now = Date.now();
  const visiblePhotos = photos.filter((photo) => {
    if (!photo.expires_at) return true;
    const expiresAt = Date.parse(photo.expires_at);
    return Number.isFinite(expiresAt) && expiresAt > now;
  });

  return {
    guestToken: sanitizeGuestTokenSummary(guestToken),
    event: sanitizeGuestEvent(event),
    photos: visiblePhotos.map(sanitizeGuestPhoto),
  };
}

async function fetchSupabaseFunction(name, body, token) {
  const response = await fetch(`${runtimeConfig.supabaseFunctionsUrl}/${name}`, {
    method: "POST",
    headers: {
      apikey: runtimeConfig.supabaseAnonKey,
      Authorization: `Bearer ${token || runtimeConfig.supabaseAnonKey}`,
      "Content-Type": "application/json",
      "x-client-info": "aevo-web",
    },
    body: JSON.stringify(body || {}),
  });

  const responseText = await response.text();
  let data = null;
  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch {
      data = { message: responseText };
    }
  }

  if (!response.ok) {
    const message = data?.error || data?.message || `Unable to call ${name}`;
    const error = new Error(message);
    error.status = response.status;
    error.code = data?.code || data?.error_code || "";
    throw error;
  }

  return data || {};
}

function isRetryableFunctionAuthError(error) {
  const message = String(error?.message || "");
  const code = String(error?.code || "");
  return (
    Number(error?.status || 0) === 401 ||
    /invalid jwt|jwt expired|unauthorized|auth session missing/i.test(message) ||
    /UNAUTHORIZED|JWT/i.test(code)
  );
}

function isCreateEventFunction(name) {
  return [
    runtimeConfig.createEventFunctionName,
    runtimeConfig.createEventCheckoutFunctionName,
  ].includes(name);
}

function getSessionExpiredMessage(name) {
  if (name === runtimeConfig.adminFunctionName) {
    return "Session admin expiree. Reconnecte-toi puis rouvre le dashboard admin.";
  }
  if (isCreateEventFunction(name)) {
    return "Session expiree. Reconnecte-toi puis relance la creation.";
  }
  return "Session expiree. Reconnecte-toi puis reessaie.";
}

function getFunctionNetworkMessage(name) {
  if (name === runtimeConfig.adminFunctionName) {
    return "Connexion a la fonction admin impossible. Garde ta session ouverte puis reessaie.";
  }
  if (isCreateEventFunction(name)) {
    return "Connexion a Supabase interrompue pendant la creation. Verifie la connexion puis reessaie.";
  }
  return "Connexion a Supabase interrompue. Verifie la connexion puis reessaie.";
}

function parseFunctionResponseText(responseText) {
  if (!responseText) return null;

  try {
    return JSON.parse(responseText);
  } catch {
    return { message: responseText };
  }
}

function createFunctionError(name, status, data) {
  const message = data?.error || data?.message || `Unable to call ${name}`;
  const error = new Error(message);
  error.status = status;
  error.code = data?.code || data?.error_code || "";
  error.detail = data?.detail || "";
  return error;
}

async function invokeSupabaseFunction(name, body, options = {}) {
  if (!supabase) {
    throw new Error("Supabase is not configured");
  }

  if (options.useAnonAuth) {
    return fetchSupabaseFunction(name, body, null);
  }

  let { data: sessionData } = await supabase.auth.getSession();
  let token = sessionData?.session?.access_token;
  const expiresAt = Number(sessionData?.session?.expires_at || 0);
  const shouldRefreshBeforeCall = token && expiresAt > 0 && expiresAt - Math.floor(Date.now() / 1000) < 90;

  if (shouldRefreshBeforeCall) {
    const { data: refreshed } = await supabase.auth.refreshSession().catch(() => ({ data: null }));
    sessionData = refreshed || sessionData;
    token = refreshed?.session?.access_token || token;
  }

  try {
    return await fetchSupabaseFunction(name, body, token);
  } catch (error) {
    const isNetworkError = error instanceof TypeError || /failed to fetch/i.test(error?.message || "");
    const shouldRefreshSession = isNetworkError || isRetryableFunctionAuthError(error);
    if (!shouldRefreshSession) throw error;

    const { data: refreshed } = await supabase.auth.refreshSession().catch(() => ({ data: null }));
    const refreshedToken = refreshed?.session?.access_token || token;

    try {
      return await fetchSupabaseFunction(name, body, refreshedToken);
    } catch (retryError) {
      if (isRetryableFunctionAuthError(retryError) && !refreshed?.session?.access_token) {
        throw new Error(getSessionExpiredMessage(name));
      }
      try {
        const { data, error: invokeError } = await supabase.functions.invoke(name, { body: body || {} });
        if (invokeError) throw invokeError;
        return data || {};
      } catch {
        if (isNetworkError || /failed to fetch/i.test(retryError?.message || "")) {
          throw new Error(getFunctionNetworkMessage(name));
        }
        throw retryError;
      }
    }
  }
}

async function getFreshFunctionToken(name) {
  let { data: sessionData } = await supabase.auth.getSession();
  let token = sessionData?.session?.access_token;
  const expiresAt = Number(sessionData?.session?.expires_at || 0);
  const shouldRefreshBeforeCall = token && expiresAt > 0 && expiresAt - Math.floor(Date.now() / 1000) < 90;

  if (shouldRefreshBeforeCall) {
    const { data: refreshed } = await supabase.auth.refreshSession().catch(() => ({ data: null }));
    sessionData = refreshed || sessionData;
    token = refreshed?.session?.access_token || token;
  }

  if (!token) {
    throw new Error(getSessionExpiredMessage(name));
  }

  return token;
}

async function invokeAdminProxy(body) {
  const name = runtimeConfig.adminFunctionName;
  const token = await getFreshFunctionToken(name);
  const response = await fetch("/api/admin-console", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "x-client-info": "aevo-web-admin",
    },
    body: JSON.stringify(body || {}),
  });

  const responseText = await response.text();
  const data = parseFunctionResponseText(responseText);

  if (!response.ok) {
    throw createFunctionError("adminConsole", response.status, data);
  }

  return data || {};
}

function shouldFallbackFromAdminProxy(error) {
  return [404, 405].includes(Number(error?.status || 0));
}

function createEntityApi(entityName) {
  return {
    filter(filters = {}, sort, limit) {
      if (entityName === "GuestToken" && Object.prototype.hasOwnProperty.call(filters, "token")) {
        throw new Error("Direct guest token lookups are disabled. Use functions.invoke('getGuestView').");
      }
      return readRecords(entityName, { filters, sort, limit });
    },
    list(sort, limit) {
      return readRecords(entityName, { sort, limit });
    },
    get(id) {
      return readRecordById(entityName, id);
    },
    create(payload) {
      return createRecord(entityName, payload);
    },
    update(id, changes) {
      return updateRecord(entityName, id, changes);
    },
    delete(id) {
      return deleteRecord(entityName, id);
    },
    subscribe(callback) {
      const localUnsubscribe = subscribeLocalEntity(entityName, callback);

      if (!supabase) return localUnsubscribe;

      const table = tableFor(entityName);
      const channel = supabase
        .channel(`souvenir:${table}:${crypto.randomUUID()}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table },
          (payload) => {
            const type =
              payload.eventType === "INSERT"
                ? "create"
                : payload.eventType === "UPDATE"
                  ? "update"
                  : "delete";

            const record = type === "delete" ? payload.old : payload.new;
            callback({
              entity: entityName,
              type,
              data: record || null,
              id: record?.id || null,
            });
          }
        )
        .subscribe();

      return () => {
        localUnsubscribe();
        supabase.removeChannel(channel);
      };
    },
  };
}

function ok(data = {}) {
  return { data };
}

async function createEvent(payload) {
  const user = await currentUser();
  if (!user?.email) return ok({ error: "Authentication required" });

  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.createEventFunctionName, payload);
    return ok({ event: data?.event || null });
  }

  const plan = pickEntitledPlan(payload.plan, user);
  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
  const options = sanitizeEventOptions(payload.options || []);
  const event = await createRecord("Event", {
    title: payload.title,
    description: payload.description || "",
    event_type: payload.event_type || payload.category || "other",
    category: payload.category || "",
    date: payload.date,
    end_date: payload.end_date || payload.date,
    location: payload.location || "",
    plan,
    cover_image_url: payload.cover_image_url || "",
    options,
    access_code: await generateUniqueAccessCode(),
    status: "active",
    visibility: "private",
    organizer_email: user.email,
    organizer_name: user.full_name || user.email,
    organizer_user_id: user.id,
    max_participants: limits.max_participants,
    max_photos_per_participant: limits.max_photos_per_participant,
    photo_count: 0,
    participant_count: 0,
  });

  await ensureParticipant(event, user, "organizer");

  const existingGiftFund = await readRecords("GiftFund", {
    filters: { event_id: event.id },
    limit: 1,
  });

  if (options.includes("gift_fund") && !existingGiftFund.length) {
    await createRecord("GiftFund", {
      event_id: event.id,
      organizer_email: user.email,
      organizer_name: user.full_name || user.email,
    });
  }

  const freshEvent = await readRecordById("Event", event.id);
  return ok({ event: freshEvent });
}

async function createPaidEventCheckout(payload) {
  const user = await currentUser();
  if (!user?.email) return ok({ error: "Session expiree. Reconnecte-toi puis relance la creation." });
  const eventDraft = {
    ...(payload || {}),
    options: sanitizeEventOptions(payload?.options || []),
  };

  if (!runtimeConfig.paymentsEnabled) {
    return ok({
      error: "Paid checkout is not enabled yet.",
      payment_required: true,
      url: null,
    });
  }

  if (!supabase) {
    return ok({
      error: "Checkout is only available on the published app.",
      payment_required: true,
      url: null,
    });
  }

  const origin = typeof window !== "undefined" ? window.location.origin : runtimeConfig.siteUrl;
  const cancelParams = new URLSearchParams({ checkout: "cancelled" });
  if (eventDraft.plan) cancelParams.set("plan", eventDraft.plan);
  const data = await invokeSupabaseFunction(runtimeConfig.createEventCheckoutFunctionName, {
    plan_code: eventDraft.plan,
    event_draft: eventDraft,
    client_request_id: eventDraft.client_request_id,
    success_url: `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    cancel_url: `${origin}/create?${cancelParams.toString()}`,
  });

  return ok({
    ...data,
    url: data?.url || data?.checkout_url || null,
    payment_required: true,
  });
}

async function getEventCheckoutStatus(payload = {}) {
  const sessionId = String(payload.session_id || "").trim();
  const checkoutId = String(payload.checkout_id || "").trim();
  if (!sessionId && !checkoutId) return ok({ error: "Checkout session is required." });

  if (supabase) {
    let query = supabase
      .from("event_checkouts")
      .select("id, plan, status, event_id, error, stripe_checkout_session_id, stripe_payment_intent_id, created_at, updated_at")
      .limit(1);

    query = sessionId ? query.eq("stripe_checkout_session_id", sessionId) : query.eq("id", checkoutId);
    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    if (!data) return ok({ status: "missing", error: "Checkout introuvable." });

    return ok({
      checkout: data,
      status: data.status,
      event_id: data.event_id || null,
      error: data.error || "",
    });
  }

  const filters = sessionId ? { stripe_checkout_session_id: sessionId } : { id: checkoutId };
  const [checkout] = await readRecords("EventCheckout", { filters, limit: 1 });
  if (!checkout) return ok({ status: "missing", error: "Checkout introuvable." });
  return ok({
    checkout,
    status: checkout.status,
    event_id: checkout.event_id || null,
    error: checkout.error || "",
  });
}

async function joinEvent(payload) {
  const code = String(payload?.access_code || "").trim().toUpperCase();
  const eventId = String(payload?.event_id || payload?.event || "").trim();
  const requestedDisplayName = String(payload?.display_name || "").trim().replace(/\s+/g, " ").slice(0, 80);
  const requestedGuestEmail = normalizeGuestEmail(payload?.guest_email || payload?.email);
  if (!code && !eventId) return ok({ error: "Event not found" });

  if (supabase) {
    try {
      const data = await invokeSupabaseFunction(runtimeConfig.joinEventFunctionName, {
        access_code: code,
        code,
        event_id: eventId,
        display_name: requestedDisplayName,
        guest_email: requestedGuestEmail,
        lookup_only: Boolean(payload?.lookup_only),
      });
      return ok(data || {});
    } catch (error) {
      console.warn("joinEvent failed:", error?.message || error);
      return ok({ error: "Event not found" });
    }
  }

  const filters = eventId ? { id: eventId } : { access_code: code };
  const [event] = await readRecords("Event", {
    filters,
    limit: 1,
  });

  if (!event) return ok({ error: "Event not found" });
  if (event.status !== "active") return ok({ error: "Event not active" });

  if (payload?.lookup_only) {
    return ok({
      preview: {
        id: event.id,
        title: event.title,
        event_type: event.event_type,
        cover_image_url: event.cover_image_url,
        location: event.location,
        participant_count: event.participant_count,
        access_code: event.access_code,
      },
    });
  }

  const user = await currentUser();
  const userEmail = normalizeGuestEmail(user?.email) || requestedGuestEmail;
  const isVerifiedUser = Boolean(user?.email);
  if (!userEmail) return ok({ error: "Guest email required" });

  const guestAccessExpiresAt = getGuestAccessExpiry(event);
  if (!isVerifiedUser && new Date(guestAccessExpiresAt) <= new Date()) {
    return ok({ error: "Verification required", verification_required: true });
  }

  const participants = await readRecords("EventParticipant", {
    filters: { event_id: event.id },
  });

  const participantDisplayName =
    requestedDisplayName ||
    user?.full_name ||
    userEmail.split("@")[0] ||
    "Participant";
  let existingParticipant = participants.find((participant) => normalizeGuestEmail(participant.user_email) === userEmail);

  if (!existingParticipant) {
    if (participants.length >= Number(event.max_participants || PLAN_LIMITS[event.plan]?.max_participants || 20)) {
      return ok({ error: "Event is full" });
    }

    existingParticipant = await createRecord("EventParticipant", {
      event_id: event.id,
      user_email: userEmail,
      display_name: participantDisplayName,
      role: "participant",
    });
  } else if (requestedDisplayName && existingParticipant.display_name !== requestedDisplayName) {
    await updateRecord("EventParticipant", existingParticipant.id, {
      display_name: requestedDisplayName,
    });
  }

  if (!isVerifiedUser) {
    const session = await createLocalGuestAccessSession({
      event,
      participant: existingParticipant,
      guestEmail: userEmail,
      displayName: participantDisplayName,
    });

    return ok({
      event: await readRecordById("Event", event.id),
      guestSession: {
        token: session.token,
        event_id: event.id,
        guest_email: userEmail,
        display_name: participantDisplayName,
        expires_at: session.expires_at,
      },
    });
  }

  return ok({ event: await readRecordById("Event", event.id) });
}

async function uploadPhoto(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.uploadPhotoFunctionName, payload, {
      useAnonAuth: Boolean(payload?.guest_session_token),
    });
    return ok(data || {});
  }

  const user = await currentUser();
  let uploadUser = user;
  let userEmail = "";
  if (payload?.guest_session_token) {
    const resolved = await resolveLocalGuestAccessSession(payload.guest_session_token, payload.event_id);
    if (resolved.error) return ok(resolved);
    userEmail = resolved.guestEmail;
    uploadUser = {
      email: resolved.session.guest_email,
      full_name: resolved.session.display_name || resolved.session.guest_email,
    };
  } else {
    userEmail = normalizeGuestEmail(user?.email);
  }
  if (!userEmail) return ok({ error: "Authentication required" });

  const event = await readRecordById("Event", payload.event_id);
  if (!event) return ok({ error: "Event not found" });

  const [participant] = await readRecords("EventParticipant", {
    filters: { event_id: event.id, user_email: userEmail },
    limit: 1,
  });

  if (!participant) return ok({ error: "Participant not found" });

  if (event.plan === "free" && payload.capture_source !== "camera") {
    return ok({
      error: "Free plan photos must be captured live with the camera.",
      camera_required: true,
    });
  }

  const limit = getMaxPhotosPerParticipant(event);
  if (Number(participant.photo_count || 0) >= limit) {
    return ok({
      limit_reached: true,
      error: "You reached the photo limit for this event.",
    });
  }

  const manualReviewPlan = !hasPlanFeature(event.plan, "ai_moderation");
  const manualReviewReason = event.plan === "standard"
    ? "Manual organizer review required on the Standard plan."
    : "Manual organizer review required on the Free plan.";
  const manualReviewFlag = event.plan === "standard" ? "standard_manual_review" : "free_manual_review";
  const photo = await createRecord("Photo", {
    event_id: event.id,
    file_url: payload.file_url,
    storage_path: payload.storage_path || "",
    caption: payload.caption || "",
    uploader_email: userEmail,
    uploader_name: payload.uploader_name || uploadUser.full_name || userEmail,
    is_visible: false,
    moderation_status: "pending_review",
    moderation_reason: manualReviewPlan ? manualReviewReason : "",
    moderation_flags: manualReviewPlan ? [manualReviewFlag] : [],
    expires_at: getPhotoExpirationDate(event)?.toISOString() || null,
  });

  const organizerEmail = String(event.organizer_email || "").trim().toLowerCase();
  const uploaderEmail = userEmail;

  if (manualReviewPlan && organizerEmail && organizerEmail !== uploaderEmail) {
    await createNotification({
      user_email: organizerEmail,
      event_id: event.id,
      event_title: event.title || "",
      type: "photo_review_required",
      actor_name: payload.uploader_name || uploadUser.full_name || userEmail,
    });
  }

  return ok({ photo_id: photo.id, photo });
}

async function moderateAndPublishPhoto(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.moderateAndPublishPhotoFunctionName, payload, {
      useAnonAuth: Boolean(payload?.guest_session_token),
    });
    return ok(data || {});
  }

  const photo = await readRecordById("Photo", payload.photo_id);
  if (!photo) return ok({ error: "Photo not found" });

  const event = await readRecordById("Event", photo.event_id);
  const user = await currentUser();
  let userEmail = "";
  if (payload?.guest_session_token) {
    const resolved = await resolveLocalGuestAccessSession(payload.guest_session_token, photo.event_id);
    if (resolved.error) return ok(resolved);
    userEmail = resolved.guestEmail;
  } else {
    userEmail = normalizeGuestEmail(user?.email);
  }

  if (!userEmail) return ok({ error: "Authentication required" });
  if (userEmail !== normalizeGuestEmail(photo.uploader_email) && userEmail !== normalizeGuestEmail(event?.organizer_email)) {
    return ok({ error: "Not allowed" });
  }

  if (!hasPlanFeature(event?.plan, "ai_moderation")) {
    const currentFlags = Array.isArray(photo.moderation_flags) ? photo.moderation_flags : [];
    const manualReviewReason = event?.plan === "standard"
      ? "Manual organizer review required on the Standard plan."
      : "Manual organizer review required on the Free plan.";
    const manualReviewFlag = event?.plan === "standard" ? "standard_manual_review" : "free_manual_review";
    const updated = await updateRecord("Photo", photo.id, {
      is_visible: false,
      moderation_status: "pending_review",
      moderation_reason: manualReviewReason,
      moderation_flags: Array.from(new Set([...currentFlags, manualReviewFlag])),
    });

    return ok({ action: "hold_for_review", flagged: false, flags: updated.moderation_flags || [], photo: updated });
  }

  const updated = await updateRecord("Photo", photo.id, {
    is_visible: true,
    moderation_status: "approved",
    moderation_reason: "",
  });

  return ok({ action: "approve", photo: updated });
}

async function moderatePhoto(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.manualModeratePhotoFunctionName, payload);
    return ok(data || {});
  }

  const photo = await readRecordById("Photo", payload.photo_id);
  if (!photo) return ok({ error: "Photo not found" });

  if (payload.action === "approve") {
    const updated = await updateRecord("Photo", photo.id, {
      is_visible: true,
      moderation_status: "approved",
      moderation_reason: "",
    });

    await createNotification({
      user_email: photo.uploader_email,
      event_id: photo.event_id,
      event_title: (await readRecordById("Event", photo.event_id))?.title || "",
      type: "moderation_result",
      actor_name: "approved",
    });

    return ok({ success: true, photo: updated });
  }

  if (payload.action === "reject") {
    const updated = await updateRecord("Photo", photo.id, {
      is_visible: false,
      moderation_status: "rejected",
      moderation_reason: "Rejected by organizer",
    });

    await createNotification({
      user_email: photo.uploader_email,
      event_id: photo.event_id,
      event_title: (await readRecordById("Event", photo.event_id))?.title || "",
      type: "moderation_result",
      actor_name: "rejected",
    });

    return ok({ success: true, photo: updated });
  }

  return ok({ error: "Unsupported moderation action" });
}

async function deletePhoto(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.deletePhotoFunctionName, payload, {
      useAnonAuth: Boolean(payload?.guest_session_token),
    });
    return ok(data || {});
  }

  const photo = await readRecordById("Photo", payload.photo_id);
  if (!photo) return ok({ success: true });

  const event = await readRecordById("Event", photo.event_id);
  const user = await currentUser();
  let userEmail = normalizeGuestEmail(user?.email);

  if (payload?.guest_session_token) {
    const resolved = await resolveLocalGuestAccessSession(payload.guest_session_token, photo.event_id);
    if (resolved.error) return ok(resolved);
    userEmail = resolved.guestEmail;
  }

  const uploaderEmail = normalizeGuestEmail(photo.uploader_email);
  const organizerEmail = normalizeGuestEmail(event?.organizer_email);
  if (!userEmail) return ok({ error: "Authentication required" });
  if (userEmail !== uploaderEmail && userEmail !== organizerEmail) {
    return ok({ error: "Not allowed" });
  }

  const isSelfDelete = userEmail === uploaderEmail && userEmail !== organizerEmail;
  let participant = null;
  if (isSelfDelete) {
    [participant] = await readRecords("EventParticipant", {
      filters: { event_id: photo.event_id, user_email: uploaderEmail },
      limit: 1,
    });
    const deleteLimit = Math.floor(Math.max(getMaxPhotosPerParticipant(event), 0) * 0.25);
    if (Number(participant?.deleted_photo_count || 0) >= deleteLimit) {
      return ok({
        error: "Photo deletion limit reached for this event.",
        limit_reached: true,
        delete_limit: deleteLimit,
      });
    }
  }

  await deleteRecord("Photo", photo.id);

  if (isSelfDelete && participant?.id) {
    await updateRecord("EventParticipant", participant.id, {
      deleted_photo_count: Number(participant.deleted_photo_count || 0) + 1,
    });
  }

  await syncParticipantPhotoCount(photo.event_id, uploaderEmail);
  await syncEventCounts(photo.event_id);
  return ok({ success: true });
}

async function deleteEventPhotos(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.deleteEventPhotosFunctionName, {
      event_id: payload.event_id,
      delete_event: false,
    });
    return ok(data || {});
  }

  const photos = await readRecords("Photo", { filters: { event_id: payload.event_id } });
  await Promise.all(photos.map((photo) => deleteRecord("Photo", photo.id)));
  await syncEventCounts(payload.event_id);
  return ok({ success: true, deleted: photos.length });
}

async function deleteEvent(payload) {
  const user = await currentUser();
  const event = await readRecordById("Event", payload.event_id);

  if (!event) return ok({ success: true });

  const isCreator =
    Boolean(user?.email && event.organizer_email === user.email) ||
    Boolean(user?.id && event.organizer_user_id === user.id);

  if (!isCreator) {
    return ok({ error: "Only the event creator can delete this event." });
  }

  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.deleteEventPhotosFunctionName, {
      event_id: event.id,
      delete_event: true,
    });
    return ok(data || {});
  }

  const photos = await readRecords("Photo", { filters: { event_id: event.id } });
  await Promise.all(photos.map((photo) => deleteRecord("Photo", photo.id)));
  await deleteRecord("Event", event.id);

  return ok({ success: true, deleted_photos: photos.length });
}

async function archiveEvent(payload) {
  await updateRecord("Event", payload.event_id, { status: "archived" });
  return ok({ success: true });
}

async function startGiftFundCheckout(payload) {
  if (!runtimeConfig.giftFundEnabled) {
    return ok({ error: "Event fund is disabled." });
  }

  const event = await readRecordById("Event", payload.event_id);
  if (!event) return ok({ error: "Event not found" });
  if (!eventHasOption(event, "gift_fund")) {
    return ok({ error: "Event fund is not active for this event." });
  }
  const amount = Math.round(Number(payload.amount || 0));
  if (!Number.isInteger(amount) || amount <= 0) return ok({ error: "Contribution amount is required." });
  const capCents = getDonationCapCents(event.plan);

  if (supabase && runtimeConfig.paymentsEnabled) {
    const origin = typeof window !== "undefined" ? window.location.origin : runtimeConfig.siteUrl;
    const data = await invokeSupabaseFunction(runtimeConfig.startGiftFundCheckoutFunctionName, {
      ...payload,
      amount,
      success_url: `${origin}/event/${payload.event_id}/gift?donation=success`,
      cancel_url: `${origin}/event/${payload.event_id}/gift?donation=cancelled`,
    });
    return ok(data || {});
  }

  const existingDonations = await readRecords("Donation", { filters: { event_id: event.id, status: "completed" } });
  const currentTotal = existingDonations.reduce((sum, donation) => sum + Number(donation.amount || 0), 0);
  if (currentTotal + amount > capCents) {
    return ok({ error: `Event fund cap reached. Remaining amount is $${((capCents - currentTotal) / 100).toFixed(2)}.` });
  }

  const user = await currentUser();
  const processingFeeAmount = calculateProcessingFeeCents(amount);
  const donation = await createRecord("Donation", {
    event_id: payload.event_id,
    gift_fund_id: payload.gift_fund_id,
    donor_name: payload.donor_name || user?.full_name || "Anonymous",
    donor_email: payload.donor_email || user?.email || "",
    message: payload.message || "",
    amount,
    processing_fee_amount: processingFeeAmount,
    total_charged_amount: amount + processingFeeAmount,
    currency: "USD",
    status: "completed",
    stripe_transfer_status: "not_configured",
  });

  await createNotification({
    user_email: event.organizer_email,
    event_id: event.id,
    event_title: event.title,
    type: "gift",
    actor_name: donation.donor_name,
  });

  return ok({ success: true, local: true, donation });
}

async function transferGiftFundDonations(payload) {
  const event = await readRecordById("Event", payload.event_id);
  if (!event) return ok({ error: "Event not found" });

  if (supabase && runtimeConfig.paymentsEnabled) {
    const data = await invokeSupabaseFunction(runtimeConfig.transferGiftFundDonationsFunctionName, {
      event_id: event.id,
    });
    return ok(data || {});
  }

  const user = await currentUser();
  const isOrganizer =
    Boolean(user?.email && event.organizer_email === user.email) ||
    Boolean(user?.id && event.organizer_user_id === user.id);

  if (!isOrganizer) return ok({ error: "Only the event creator can transfer event fund contributions." });

  const [payoutAccount] = await readRecords("OrganizerPayoutAccount", {
    filters: { organizer_email: event.organizer_email },
    limit: 1,
  });

  if (!payoutAccount || payoutAccount.status !== "ready" || !payoutAccount.payouts_enabled) {
    return ok({ error: "Complete Stripe Connect before transferring event fund contributions." });
  }

  const donations = await readRecords("Donation", { filters: { event_id: event.id, status: "completed" } });
  const pending = donations.filter((donation) => donation.stripe_transfer_status !== "transferred");
  const getTransferAmount = (donation) => Math.min(
    Number(donation.stripe_net_amount || donation.amount || 0),
    Number(donation.amount || 0)
  );
  const totalAmount = pending.reduce((sum, donation) => sum + getTransferAmount(donation), 0);

  if (!pending.length || totalAmount <= 0) {
    return ok({ success: true, transferred: 0, amount: 0 });
  }

  await Promise.all(pending.map((donation) => updateRecord("Donation", donation.id, {
    organizer_stripe_account_id: payoutAccount.stripe_account_id,
    stripe_transfer_destination: payoutAccount.stripe_account_id,
    stripe_transfer_status: "transferred",
    stripe_transfer_amount: getTransferAmount(donation),
    stripe_transferred_at: nowIso(),
  })));

  return ok({ success: true, local: true, transferred: pending.length, amount: totalAmount });
}

async function startConnectOnboarding(payload = {}) {
  if (!runtimeConfig.paymentsEnabled) {
    return ok({ error: "Payments are not enabled yet." });
  }

  if (supabase) {
    const origin = typeof window !== "undefined" ? window.location.origin : runtimeConfig.siteUrl;
    const returnPath = payload.event_id ? `/event/${payload.event_id}/organizer?payout=return` : "/dashboard?payout=return";
    const refreshPath = payload.event_id ? `/event/${payload.event_id}/organizer?payout=refresh` : "/dashboard?payout=refresh";
    const data = await invokeSupabaseFunction(runtimeConfig.startConnectOnboardingFunctionName, {
      ...payload,
      return_url: `${origin}${returnPath}`,
      refresh_url: `${origin}${refreshPath}`,
    });
    return ok(data || {});
  }

  const user = await currentUser();
  if (!user?.email) return ok({ error: "Authentication required" });
  const existing = await readRecords("OrganizerPayoutAccount", {
    filters: { organizer_email: user.email },
    limit: 1,
  });

  if (existing[0]) return ok({ status: existing[0].status, account: existing[0] });

  const account = await createRecord("OrganizerPayoutAccount", {
    organizer_user_id: user.id,
    organizer_email: user.email,
    stripe_account_id: `acct_local_${crypto.randomUUID().replace(/-/g, "").slice(0, 16)}`,
    status: "ready",
    charges_enabled: true,
    payouts_enabled: true,
    details_submitted: true,
  });

  return ok({ status: "ready", account });
}

async function refreshConnectAccount() {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.refreshConnectAccountFunctionName, {});
    return ok(data || {});
  }

  const user = await currentUser();
  if (!user?.email) return ok({ error: "Authentication required" });
  const [account] = await readRecords("OrganizerPayoutAccount", {
    filters: { organizer_email: user.email },
    limit: 1,
  });
  return ok(account || { status: "not_configured" });
}

async function markNotificationRead(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.manageNotificationsFunctionName, {
      action: "mark_read",
      id: payload.id,
    });
    return ok(data || {});
  }

  await updateRecord("Notification", payload.id, { is_read: true });
  return ok({ success: true });
}

async function markAllNotificationsRead() {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.manageNotificationsFunctionName, {
      action: "mark_all_read",
    });
    return ok(data || {});
  }

  const user = await currentUser();
  if (!user?.email) return ok({ error: "Authentication required" });
  const notifications = await readRecords("Notification", { filters: { user_email: user.email } });
  await Promise.all(notifications.filter((item) => !item.is_read).map((item) => updateRecord("Notification", item.id, { is_read: true })));
  return ok({ success: true, updated: notifications.length });
}

async function deleteNotification(payload) {
  if (supabase) {
    try {
      const data = await invokeSupabaseFunction(runtimeConfig.manageNotificationsFunctionName, {
        action: "delete",
        id: payload.id,
      });
      return ok(data || {});
    } catch (error) {
      console.warn("deleteNotification function failed, trying direct delete:", error?.message || error);
      await deleteRecord("Notification", payload.id);
      return ok({ success: true, fallback: "direct_delete" });
    }
  }

  await deleteRecord("Notification", payload.id);
  return ok({ success: true });
}

async function generateBestOf(payload) {
  const event = await readRecordById("Event", payload.event_id);
  if (!event) throw new Error("Event not found");
  if (!hasPlanFeature(event.plan, "best_of")) {
    return ok({ error: "Best Of requires a Standard, Plus, or Premium plan." });
  }

  const limit = Number(payload.limit || 12);
  const style = payload.style || "elegant";
  const photos = (await readRecords("Photo", {
    filters: { event_id: event.id, is_visible: true },
  })).filter(isApprovedVisiblePhoto);
  const picks = await readRecords("EventPhotoPick", { filters: { event_id: event.id } });

  const ranked = rankMoments(photos, {
    mode: "bestof",
    pickedIds: picks.map((pick) => pick.photo_id),
    limit,
  })
    .map((photo, index) => ({
      ...photo,
      score: Number(photo.moment_score || 0),
      index: index + 1,
      ai_caption:
        photo.caption ||
        (style === "emotional"
          ? "A moment worth keeping forever"
          : style === "fun"
            ? "A highlight from the celebration"
            : "One of the strongest memories from this event"),
    }));

  const totalReactions = ranked.reduce(
    (sum, photo) =>
      sum +
      Number(photo.heart_count || 0) +
      Number(photo.funny_count || 0) +
      Number(photo.shocked_count || 0),
    0
  );

  const avgScore = ranked.length
    ? Number(
        (
          ranked.reduce((sum, photo) => sum + Number(photo.score || 0), 0) / ranked.length
        ).toFixed(1)
      )
    : 0;

  return ok({
    event_id: event.id,
    event_title: event.title,
    total_slides: ranked.length,
    slides: ranked,
    stats: {
      total_reactions: totalReactions,
      avg_score: avgScore,
    },
    narrative: {
      title: `Best of ${event.title}`,
      subtitle:
        style === "emotional"
          ? "A gentle recap of the strongest memories"
          : style === "fun"
            ? "The most joyful moments in one sequence"
            : "A curated recap of the event highlights",
      opening_text: "These photos were selected from the moments that resonated the most.",
      closing_text: "Thank you for being part of this memory.",
    },
  });
}

async function resolveLocalMomentViewer(payload, eventId) {
  if (payload?.guest_session_token) {
    const resolved = await resolveLocalGuestAccessSession(payload.guest_session_token, eventId);
    if (resolved.error) return { error: resolved.error };
    return {
      email: resolved.guestEmail,
      event: resolved.event,
      isOrganizer: false,
      identityKey: `guest:${resolved.session.id}`,
    };
  }

  const user = await currentUser();
  const userEmail = normalizeEmail(user?.email);
  if (!userEmail) return { error: "Authentication required" };
  const event = await readRecordById("Event", eventId);
  if (!event) return { error: "Event not found" };
  const isOrganizer = normalizeEmail(event.organizer_email) === userEmail;
  const participants = isOrganizer
    ? []
    : await readRecords("EventParticipant", { filters: { event_id: eventId } });
  const isParticipant = participants.some((item) => normalizeEmail(item.user_email) === userEmail);
  if (!isOrganizer && !isParticipant) return { error: "Event access required" };

  return {
    email: userEmail,
    event,
    isOrganizer,
    identityKey: `user:${user?.id || userEmail}`,
  };
}

async function refreshLocalMomentPhoto(photoId) {
  const photo = await readRecordById("Photo", photoId);
  if (!photo) return null;
  const reactions = await readRecords("Reaction", { filters: { photo_id: photoId } });
  const favorites = await readRecords("PhotoFavorite", { filters: { photo_id: photoId } });
  const counts = {
    heart_count: reactions.filter((item) => item.type === "heart").length,
    funny_count: reactions.filter((item) => item.type === "funny").length,
    shocked_count: reactions.filter((item) => item.type === "shocked").length,
    favorite_count: favorites.length,
  };
  const engagementScore =
    counts.heart_count * 3
    + counts.funny_count * 2
    + counts.shocked_count * 2
    + counts.favorite_count * 4
    + Math.min(Number(photo.view_count || 0), 500) * 0.25
    + Number(photo.share_count || 0) * 3;

  return updateRecord("Photo", photoId, {
    ...counts,
    engagement_score: engagementScore,
    last_engaged_at: nowIso(),
  });
}

async function manageMoments(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.manageMomentsFunctionName, payload, {
      useAnonAuth: Boolean(payload?.guest_session_token),
    });
    return ok(data || {});
  }

  const action = String(payload?.action || "").trim().toLowerCase();
  const photo = payload?.photo_id ? await readRecordById("Photo", payload.photo_id) : null;
  const eventId = String(photo?.event_id || payload?.event_id || "").trim();
  if (!eventId) return ok({ error: "event_id is required" });
  if (payload?.event_id && photo && payload.event_id !== photo.event_id) {
    return ok({ error: "Photo does not belong to this event" });
  }

  const viewer = await resolveLocalMomentViewer(payload, eventId);
  if (viewer.error) return ok({ error: viewer.error });
  const eventPhotos = await readRecords("Photo", { filters: { event_id: eventId } });
  const eventPhotoIds = new Set(eventPhotos.map((item) => item.id));

  if (action === "list") {
    const reactions = (await readRecords("Reaction", { filters: { user_email: viewer.email } }))
      .filter((item) => eventPhotoIds.has(item.photo_id));
    const favorites = await readRecords("PhotoFavorite", {
      filters: { event_id: eventId, user_email: viewer.email },
    });
    const picks = await readRecords("EventPhotoPick", { filters: { event_id: eventId }, sort: "sort_order" });
    const allMissions = await readRecords("PhotoMission", { filters: { event_id: eventId }, sort: "sort_order" });
    const now = Date.now();
    const missions = viewer.isOrganizer
      ? allMissions
      : allMissions.filter((mission) => {
          const startsAt = mission.starts_at ? Date.parse(mission.starts_at) : null;
          const endsAt = mission.ends_at ? Date.parse(mission.ends_at) : null;
          return mission.status === "active"
            && (!Number.isFinite(startsAt) || startsAt <= now)
            && (!Number.isFinite(endsAt) || endsAt >= now);
        });
    const submissions = (await readRecords("PhotoMissionSubmission", { filters: { event_id: eventId } }))
      .filter((item) => viewer.isOrganizer || normalizeEmail(item.submitted_by) === viewer.email);
    const reports = viewer.isOrganizer
      ? (await readRecords("PhotoReport", { filters: { event_id: eventId, status: "open" }, sort: "-created_at" }))
      : [];

    return ok({
      reactions,
      favorite_photo_ids: favorites.map((item) => item.photo_id),
      picks,
      missions,
      submissions,
      reports,
      capabilities: {
        can_pick: viewer.isOrganizer && hasPlanFeature(viewer.event.plan, "best_of"),
        can_manage_missions: viewer.isOrganizer && hasPlanFeature(viewer.event.plan, "photo_missions"),
      },
    });
  }

  if (action === "mission_create") {
    if (!viewer.isOrganizer) return ok({ error: "Organizer access required" });
    if (!hasPlanFeature(viewer.event.plan, "photo_missions")) {
      return ok({ error: "Photo Missions requires a Plus or Premium plan" });
    }
    const title = String(payload?.title || "").trim().slice(0, 100);
    if (!title) return ok({ error: "Mission title is required" });
    const mission = await createRecord("PhotoMission", {
      event_id: eventId,
      title,
      description: String(payload?.description || "").trim().slice(0, 300),
      status: "active",
      starts_at: payload?.starts_at || null,
      ends_at: payload?.ends_at || null,
      created_by: viewer.email,
    });
    return ok({ success: true, mission });
  }

  if (action === "mission_close") {
    if (!viewer.isOrganizer) return ok({ error: "Organizer access required" });
    const mission = await readRecordById("PhotoMission", payload?.mission_id);
    if (!mission || mission.event_id !== eventId) return ok({ error: "Mission not found" });
    return ok({ success: true, mission: await updateRecord("PhotoMission", mission.id, { status: "closed" }) });
  }

  if (!photo) return ok({ error: "Photo not found" });
  const isAvailable = photo.is_visible === true && photo.moderation_status === "approved";
  if (!viewer.isOrganizer && !isAvailable) return ok({ error: "Photo unavailable" });

  if (action === "react") {
    const reactionType = String(payload?.reaction_type || "").trim().toLowerCase();
    if (!REACTION_TYPES.some((reaction) => reaction.type === reactionType)) {
      return ok({ error: "Unsupported reaction" });
    }
    const existing = (await readRecords("Reaction", { filters: { photo_id: photo.id } }))
      .find((item) => normalizeEmail(item.user_email) === viewer.email && item.type === reactionType);
    const active = payload?.active !== false;
    if (active && !existing) {
      await createRecord("Reaction", { photo_id: photo.id, user_email: viewer.email, type: reactionType });
    }
    if (!active && existing) await deleteRecord("Reaction", existing.id);
    return ok({ success: true, active, photo: await refreshLocalMomentPhoto(photo.id) });
  }

  if (action === "favorite") {
    const existing = (await readRecords("PhotoFavorite", { filters: { photo_id: photo.id } }))
      .find((item) => normalizeEmail(item.user_email) === viewer.email);
    const active = payload?.active !== false;
    if (active && !existing) {
      await createRecord("PhotoFavorite", {
        event_id: eventId,
        photo_id: photo.id,
        user_email: viewer.email,
      });
    }
    if (!active && existing) await deleteRecord("PhotoFavorite", existing.id);
    return ok({ success: true, active, photo: await refreshLocalMomentPhoto(photo.id) });
  }

  if (action === "view") {
    const db = ensureLocalDb();
    const viewerKey = `${photo.id}:${viewer.identityKey}`;
    const existing = db.photo_view_sessions.find((item) => item.viewer_key === viewerKey);
    if (!existing) {
      db.photo_view_sessions.push({ id: crypto.randomUUID(), event_id: eventId, photo_id: photo.id, viewer_key: viewerKey, created_at: nowIso() });
      saveLocalDb(db);
      const updated = await updateRecord("Photo", photo.id, { view_count: Number(photo.view_count || 0) + 1 });
      await refreshLocalMomentPhoto(photo.id);
      return ok({ success: true, view_count: updated.view_count });
    }
    return ok({ success: true, view_count: Number(photo.view_count || 0) });
  }

  if (action === "share") {
    const updated = await updateRecord("Photo", photo.id, { share_count: Number(photo.share_count || 0) + 1 });
    await refreshLocalMomentPhoto(photo.id);
    return ok({ success: true, share_count: updated.share_count });
  }

  if (action === "pick") {
    if (!viewer.isOrganizer) return ok({ error: "Organizer access required" });
    if (!hasPlanFeature(viewer.event.plan, "best_of")) return ok({ error: "Best Of requires a paid plan" });
    const existing = (await readRecords("EventPhotoPick", { filters: { event_id: eventId, photo_id: photo.id } }))[0];
    const selected = payload?.selected !== false;
    if (selected && !existing) {
      await createRecord("EventPhotoPick", { event_id: eventId, photo_id: photo.id, picked_by: viewer.email });
    }
    if (!selected && existing) await deleteRecord("EventPhotoPick", existing.id);
    return ok({ success: true, selected });
  }

  if (action === "report") {
    if (viewer.isOrganizer) return ok({ error: "Organizers can moderate photos directly" });
    const reason = String(payload?.reason || "other").trim().toLowerCase();
    const allowedReasons = ["inappropriate", "privacy", "spam", "other"];
    if (!allowedReasons.includes(reason)) return ok({ error: "Unsupported report reason" });
    const existing = (await readRecords("PhotoReport", { filters: { event_id: eventId, photo_id: photo.id } }))
      .find((item) => item.reporter_key === viewer.identityKey);
    if (!existing) {
      await createRecord("PhotoReport", {
        event_id: eventId,
        photo_id: photo.id,
        reporter_hash: viewer.identityKey,
        reporter_key: viewer.identityKey,
        reason,
        details: String(payload?.details || "").trim().slice(0, 500),
        status: "open",
      });
    }
    return ok({ success: true });
  }

  if (action === "mission_submit") {
    const mission = await readRecordById("PhotoMission", payload?.mission_id);
    if (!mission || mission.event_id !== eventId || mission.status !== "active") {
      return ok({ error: "Mission unavailable" });
    }
    if (!viewer.isOrganizer && normalizeEmail(photo.uploader_email) !== viewer.email) {
      return ok({ error: "Only your own photo can complete a mission" });
    }
    const existing = (await readRecords("PhotoMissionSubmission", {
      filters: { mission_id: mission.id, photo_id: photo.id },
    }))[0];
    if (!existing) {
      await createRecord("PhotoMissionSubmission", {
        event_id: eventId,
        mission_id: mission.id,
        photo_id: photo.id,
        submitted_by: viewer.email,
      });
    }
    return ok({ success: true });
  }

  return ok({ error: "Unsupported Moments action" });
}

async function getGuestView(payload) {
  const tokenValue = String(payload?.token || "").trim();
  const guestSessionToken = String(payload?.guest_session_token || "").trim();
  if (!tokenValue && !guestSessionToken) return ok({ error: "Lien invalide ou expire." });

  if (!supabase) {
    if (guestSessionToken) {
      const resolved = await resolveLocalGuestAccessSession(guestSessionToken);
      if (resolved.error) return ok(resolved);

      const photos = await readRecords("Photo", {
        filters: { event_id: resolved.event.id },
        sort: "-created_date",
      });
      const now = Date.now();
      const visiblePhotos = photos.filter((photo) => {
        const isOwn = normalizeGuestEmail(photo.uploader_email) === resolved.guestEmail;
        if (isOwn) return true;
        if (!isApprovedVisiblePhoto(photo)) return false;
        if (!photo.expires_at) return true;
        const expiresAt = Date.parse(photo.expires_at);
        return Number.isFinite(expiresAt) && expiresAt > now;
      });

      return ok({
        guestSession: {
          event_id: resolved.event.id,
          guest_email: resolved.session.guest_email,
          display_name: resolved.session.display_name,
          expires_at: resolved.session.expires_at,
        },
        event: sanitizeGuestEvent(resolved.event),
        photos: visiblePhotos.map(sanitizeGuestPhoto),
      });
    }

    return ok(await resolveGuestViewFromDataStore(tokenValue));
  }

  try {
    const data = await invokeSupabaseFunction(runtimeConfig.guestGalleryFunctionName, {
      token: tokenValue,
      guest_session_token: guestSessionToken,
      screen_mode: payload?.screen_mode === true,
      track_view: payload?.track_view !== false,
      limit: payload?.limit,
    });
    return ok(data || {});
  } catch (error) {
    if (!runtimeConfig.allowInsecureDirectGuestRead) {
      return ok({
        error:
          "La galerie invitee n'est pas disponible tant que la fonction publique n'est pas deployee.",
      });
    }

    return ok(await resolveGuestViewFromDataStore(tokenValue));
  }
}

async function deleteAccount() {
  const user = await currentUser();
  if (!user?.email) return ok({ success: true });

  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.deleteAccountFunctionName, {});
    if (data?.success) await supabase.auth.signOut().catch(() => {});
    return ok(data || {});
  }

  const organizedEvents = await readRecords("Event", {
    filters: { organizer_email: user.email },
  });

  for (const event of organizedEvents) {
    const eventPhotos = await readRecords("Photo", { filters: { event_id: event.id } });
    await Promise.all(eventPhotos.map((photo) => deleteRecord("Photo", photo.id)));

    const eventParticipants = await readRecords("EventParticipant", { filters: { event_id: event.id } });
    await Promise.all(eventParticipants.map((participant) => deleteRecord("EventParticipant", participant.id)));

    const guestTokens = await readRecords("GuestToken", { filters: { event_id: event.id } });
    await Promise.all(guestTokens.map((token) => deleteRecord("GuestToken", token.id)));

    const donations = await readRecords("Donation", { filters: { event_id: event.id } });
    await Promise.all(donations.map((donation) => deleteRecord("Donation", donation.id)));

    const giftFunds = await readRecords("GiftFund", { filters: { event_id: event.id } });
    await Promise.all(giftFunds.map((giftFund) => deleteRecord("GiftFund", giftFund.id)));

    const favorites = await readRecords("PhotoFavorite", { filters: { event_id: event.id } });
    await Promise.all(favorites.map((favorite) => deleteRecord("PhotoFavorite", favorite.id)));

    const picks = await readRecords("EventPhotoPick", { filters: { event_id: event.id } });
    await Promise.all(picks.map((pick) => deleteRecord("EventPhotoPick", pick.id)));

    const missions = await readRecords("PhotoMission", { filters: { event_id: event.id } });
    await Promise.all(missions.map((mission) => deleteRecord("PhotoMission", mission.id)));

    const submissions = await readRecords("PhotoMissionSubmission", { filters: { event_id: event.id } });
    await Promise.all(submissions.map((submission) => deleteRecord("PhotoMissionSubmission", submission.id)));

    const reports = await readRecords("PhotoReport", { filters: { event_id: event.id } });
    await Promise.all(reports.map((report) => deleteRecord("PhotoReport", report.id)));

    await deleteRecord("Event", event.id);
  }

  const myPhotos = await readRecords("Photo", { filters: { uploader_email: user.email } });
  await Promise.all(myPhotos.map((photo) => deleteRecord("Photo", photo.id)));

  const myParticipations = await readRecords("EventParticipant", {
    filters: { user_email: user.email },
  });
  await Promise.all(myParticipations.map((participant) => deleteRecord("EventParticipant", participant.id)));

  const myReactions = await readRecords("Reaction", { filters: { user_email: user.email } });
  await Promise.all(myReactions.map((reaction) => deleteRecord("Reaction", reaction.id)));

  const myFavorites = await readRecords("PhotoFavorite", { filters: { user_email: user.email } });
  await Promise.all(myFavorites.map((favorite) => deleteRecord("PhotoFavorite", favorite.id)));

  const mySubmissions = await readRecords("PhotoMissionSubmission", { filters: { submitted_by: user.email } });
  await Promise.all(mySubmissions.map((submission) => deleteRecord("PhotoMissionSubmission", submission.id)));

  const myGuestSessions = await readRecords("GuestAccessSession", { filters: { guest_email: user.email } });
  await Promise.all(myGuestSessions.map((session) => deleteRecord("GuestAccessSession", session.id)));

  const myNotifications = await readRecords("Notification", {
    filters: { user_email: user.email },
  });
  await Promise.all(myNotifications.map((notification) => deleteRecord("Notification", notification.id)));

  const mySupportTickets = await readRecords("SupportTicket", { filters: { user_email: user.email } });
  for (const ticket of mySupportTickets) {
    const messages = await readRecords("SupportTicketMessage", { filters: { ticket_id: ticket.id } });
    await Promise.all(messages.map((message) => deleteRecord("SupportTicketMessage", message.id)));
    await deleteRecord("SupportTicket", ticket.id);
  }

  const myPayoutAccounts = await readRecords("OrganizerPayoutAccount", { filters: { organizer_email: user.email } });
  await Promise.all(myPayoutAccounts.map((account) => deleteRecord("OrganizerPayoutAccount", account.id)));

  const myDonations = await readRecords("Donation", { filters: { donor_email: user.email } });
  await Promise.all(myDonations.map((donation) => updateRecord("Donation", donation.id, {
    donor_email: "",
    donor_name: "Deleted user",
  })));

  const myCheckouts = await readRecords("EventCheckout", { filters: { user_email: user.email } });
  await Promise.all(myCheckouts.map((checkout) => updateRecord("EventCheckout", checkout.id, {
    user_email: "",
    user_name: "Deleted user",
  })));

  if (supabase) {
    await supabase.auth.signOut();
  }
  writeLocalUser(null);
  return ok({ success: true });
}

async function sendWelcomeEmail(payload) {
  if (!runtimeConfig.emailsEnabled || !supabase) {
    return ok({ success: true, skipped: true, reason: "email_disabled" });
  }

  try {
    const data = await invokeSupabaseFunction(runtimeConfig.welcomeEmailFunctionName, payload);
    return ok(data || { success: true });
  } catch (error) {
    console.warn("sendWelcomeEmail failed:", error?.message || error);
    return ok({
      success: false,
      skipped: true,
      reason: "email_failed",
      message: error?.message || "Unable to send welcome email",
    });
  }
}

async function createSupportTicket(payload = {}) {
  const user = await currentUser();
  if (!user?.email) return ok({ error: "Authentication required" });

  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.createSupportTicketFunctionName, payload);
    return ok(data || {});
  }

  const now = nowIso();
  const subject = String(payload.subject || "").trim();
  const message = String(payload.message || "").trim();
  if (!subject || !message) return ok({ error: "subject and message are required" });

  const ticket = await createRecord("SupportTicket", {
    user_email: user.email,
    user_id: user.id,
    user_name: user.full_name || user.email,
    event_id: payload.event_id || null,
    category: payload.category || "support",
    priority: payload.priority || "normal",
    subject,
    description: message,
    status: "open",
    last_message_at: now,
  });

  const firstMessage = await createRecord("SupportTicketMessage", {
    ticket_id: ticket.id,
    sender_email: user.email,
    sender_name: user.full_name || user.email,
    sender_role: "customer",
    message,
  });

  return ok({ success: true, ticket, message: firstMessage, email: { skipped: true, reason: "local_mode" } });
}

async function adminConsole(payload = {}) {
  const user = await currentUser();
  if (!user?.email) return ok({ error: "Authentication required" });

  if (supabase) {
    let data = null;
    try {
      data = await invokeAdminProxy(payload);
    } catch (proxyError) {
      if (!shouldFallbackFromAdminProxy(proxyError)) throw proxyError;
      data = await invokeSupabaseFunction(runtimeConfig.adminFunctionName, payload);
    }
    return ok(data || {});
  }

  if (user.role !== "admin") return ok({ error: "Admin access required" });

  const action = String(payload?.action || "dashboard");
  if (action === "set_event_plan") {
    const plan = sanitizeRequestedPlan(payload.plan);
    const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
    const row = await updateRecord("Event", payload.event_id || payload.id, {
      plan,
      max_participants: limits.max_participants,
      max_photos_per_participant: limits.max_photos_per_participant,
    });
    return ok({ success: true, row });
  }

  if (action === "set_user_plan") {
    const plan = sanitizeRequestedPlan(payload.plan);
    const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
    const email = String(payload.email || "").trim().toLowerCase();
    const events = await readRecords("Event", { filters: { organizer_email: email } });
    const activeEvents = events.filter((event) => ["active", "funding"].includes(event.status));
    await Promise.all(activeEvents.map((event) => updateRecord("Event", event.id, {
      plan,
      max_participants: limits.max_participants,
      max_photos_per_participant: limits.max_photos_per_participant,
    })));
    return ok({ success: true, updated: activeEvents.length });
  }

  if (action === "update") {
    const entity = Object.entries(ENTITY_TABLES).find(([, table]) => table === payload.table)?.[0];
    if (!entity) return ok({ error: "Unsupported admin table" });
    const row = await updateRecord(entity, payload.id, payload.changes || {});
    return ok({ success: true, row });
  }

  if (action === "support_reply") {
    const message = String(payload.message || "").trim();
    if (!payload.ticket_id || !message) return ok({ error: "ticket_id and message are required" });
    const reply = await createRecord("SupportTicketMessage", {
      ticket_id: payload.ticket_id,
      sender_email: user.email,
      sender_name: user.full_name || user.email,
      sender_role: "support",
      message,
    });
    await updateRecord("SupportTicket", payload.ticket_id, {
      status: payload.status || "waiting_on_customer",
      assigned_to_email: user.email,
      last_message_at: nowIso(),
    });
    return ok({ success: true, reply });
  }

  if (action === "delete") {
    const entity = Object.entries(ENTITY_TABLES).find(([, table]) => table === payload.table)?.[0];
    if (!entity) return ok({ error: "Unsupported admin table" });
    await deleteRecord(entity, payload.id);
    return ok({ success: true });
  }

  const db = ensureLocalDb();
  const tables = Object.keys(defaultCollections());
  const totalStorage = db.photos.reduce((sum, photo) => sum + Number(photo.file_size || 0), 0);
  return ok({
    is_admin: true,
    analytics: {
      total_events: db.events.length,
      active_events: db.events.filter((event) => event.status === "active").length,
      archived_events: db.events.filter((event) => event.status === "archived").length,
      total_photos: db.photos.length,
      visible_photos: db.photos.filter((photo) => photo.is_visible !== false).length,
      pending_photos: db.photos.filter((photo) => photo.moderation_status === "pending_review").length,
      total_participants: db.event_participants.length,
      total_reactions: db.reactions.length,
      total_donations: db.donations.length,
      donation_amount: db.donations.reduce((sum, donation) => sum + Number(donation.amount || 0), 0),
      total_payout_accounts: db.organizer_payout_accounts.length,
      total_support_tickets: db.support_tickets.length,
      open_support_tickets: db.support_tickets.filter((ticket) => !["resolved", "closed"].includes(ticket.status)).length,
      urgent_support_tickets: db.support_tickets.filter((ticket) => ["high", "urgent"].includes(ticket.priority) && !["resolved", "closed"].includes(ticket.status)).length,
      ready_payout_accounts: db.organizer_payout_accounts.filter((account) =>
        account.status === "ready" && account.charges_enabled && account.payouts_enabled
      ).length,
      storage_bytes: totalStorage,
      total_audit_logs: db.audit_logs.length,
    },
    audit_logs: clone(db.audit_logs).slice(0, 50),
    tables: Object.fromEntries(tables.map((table) => [table, clone(db[table]).slice(0, 100)])),
    users: user ? [user] : [],
  });
}

const functionHandlers = {
  createEvent,
  createPaidEventCheckout,
  getEventCheckoutStatus,
  joinEvent,
  createGuestUploadUrl,
  uploadPhoto,
  moderateAndPublishPhoto,
  moderatePhoto,
  deletePhoto,
  deleteEventPhotos,
  deleteEvent,
  archiveEvent,
  startGiftFundCheckout,
  transferGiftFundDonations,
  startConnectOnboarding,
  refreshConnectAccount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  generateBestOf,
  manageMoments,
  getGuestView,
  deleteAccount,
  sendWelcomeEmail,
  createSupportTicket,
  adminConsole,
};

async function createGuestUploadUrl(payload) {
  if (supabase) {
    const data = await invokeSupabaseFunction(runtimeConfig.createGuestUploadFunctionName, payload);
    return ok(data || {});
  }
  return ok({
    path: "",
    token: "",
    file_url: "",
  });
}

async function uploadFile({ file, guestSession } = {}) {
  if (!file) throw new Error("No file provided");

  if (!supabase) {
    return {
      file_url: await toDataUrl(file),
      storage_path: "",
    };
  }

  const bucket = runtimeConfig.supabaseStorageBucket;
  if (guestSession?.token) {
    const eventId = guestSession.eventId || guestSession.event_id;
    if (!eventId) throw new Error("Event not found");

    const uploadUrlResult = await createGuestUploadUrl({
      event_id: eventId,
      guest_session_token: guestSession.token,
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
    });
    const uploadUrl = uploadUrlResult.data || {};
    if (uploadUrl.error || !uploadUrl.path || !uploadUrl.token) {
      throw new Error(uploadUrl.error || "Unable to create guest upload URL");
    }

    const { error } = await supabase.storage
      .from(bucket)
      .uploadToSignedUrl(uploadUrl.path, uploadUrl.token, file);

    if (error) throw error;

    return {
      file_url: uploadUrl.file_url || supabase.storage.from(bucket).getPublicUrl(uploadUrl.path).data.publicUrl,
      storage_path: uploadUrl.path,
    };
  }

  const user = await currentUser();
  const path = `${user?.id || "guest"}/${Date.now()}-${sanitizeFileName(file.name)}`;

  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: "3600",
    contentType: file.type || "application/octet-stream",
    upsert: false,
  });

  if (error) throw error;

  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return {
    file_url: data.publicUrl,
    storage_path: path,
  };
}

async function signIn({ email, fullName, redirectTo, lang, shouldCreateUser = true }) {
  const safeEmail = String(email || "").trim().toLowerCase();
  if (!safeEmail) throw new Error("Email is required");

  if (!supabase) {
    const localUser = {
      id: crypto.randomUUID(),
      email: safeEmail,
      full_name: String(fullName || "").trim() || safeEmail.split("@")[0],
      role: isRuntimeAdminEmail(safeEmail) ? "admin" : "member",
    };
    writeLocalUser(localUser);
    return { mode: "local", user: localUser };
  }

  const redirectTarget =
    redirectTo ||
    new URL("/auth", window.location.origin).toString();

  const { error } = await supabase.auth.signInWithOtp({
    email: safeEmail,
    options: {
      emailRedirectTo: redirectTarget,
      shouldCreateUser: Boolean(shouldCreateUser),
      data: {
        full_name: String(fullName || "").trim() || safeEmail.split("@")[0],
        lang: String(lang || "fr").trim().toLowerCase(),
      },
    },
  });

  if (error) throw error;
  return { mode: "magic_link" };
}

async function resolveDashboardRedirect({ user } = {}) {
  const current = user || (await currentUser());
  if (!current?.email) return "/dashboard";

  if (String(current.role || "").toLowerCase() === "admin") {
    return "/admin";
  }

  try {
    const organizedEvents = await readRecords("Event", {
      filters: { organizer_email: current.email },
      sort: "-created_date",
      limit: 2,
    });

    if (organizedEvents.length === 1) {
      return `/event/${organizedEvents[0].id}/organizer`;
    }

    if (organizedEvents.length > 1) {
      return "/dashboard?tab=organized";
    }

    const participations = await readRecords("EventParticipant", {
      filters: { user_email: current.email },
      sort: "-created_date",
      limit: 2,
    });

    if (participations.length === 1 && participations[0]?.event_id) {
      return `/event/${participations[0].event_id}`;
    }
  } catch (error) {
    console.warn("Unable to resolve dashboard redirect:", error?.message || error);
  }

  return "/dashboard";
}

async function signInWithProvider({ provider, redirectTo }) {
  const safeProvider = String(provider || "").trim().toLowerCase();
  if (safeProvider !== "google") {
    throw new Error("Unsupported sign-in provider");
  }

  if (!supabase) {
    throw new Error("Google sign-in requires hosted authentication");
  }

  const redirectTarget =
    redirectTo ||
    new URL("/auth", window.location.origin).toString();

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: safeProvider,
    options: {
      redirectTo: redirectTarget,
      queryParams: {
        prompt: "select_account",
      },
    },
  });

  if (error) throw error;
  return { mode: "oauth", provider: safeProvider, url: data?.url || "" };
}

async function refreshSession() {
  if (!supabase) return readLocalUser();

  const { data, error } = await supabase.auth.refreshSession();
  if (error) throw error;
  return normalizeSupabaseUser(data?.user || data?.session?.user || null);
}

function redirectToLogin(returnTo) {
  const redirect = returnTo || window.location.href;
  const target = `/auth?redirect=${encodeURIComponent(redirect)}`;
  window.location.assign(target);
}

async function logout(returnTo) {
  if (supabase) {
    await supabase.auth.signOut();
  }
  writeLocalUser(null);

  if (returnTo) {
    window.location.assign(returnTo);
  }
}

function subscribeAuth(callback) {
  let active = true;

  const emit = async () => {
    if (!active) return;
    callback(await currentUser());
  };

  const onStorage = (event) => {
    if (event.key === LOCAL_USER_KEY) emit();
  };

  const onLocalChange = () => emit();

  window.addEventListener("storage", onStorage);
  window.addEventListener(AUTH_EVENT, onLocalChange);

  let unsubscribeSupabase = () => {};
  if (supabase) {
    const { data } = supabase.auth.onAuthStateChange(() => {
      emit();
    });
    unsubscribeSupabase = () => data.subscription.unsubscribe();
  }

  return () => {
    active = false;
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(AUTH_EVENT, onLocalChange);
    unsubscribeSupabase();
  };
}

export const appClient = {
  runtime: {
    mode: supabase ? "supabase" : "local",
    supportEmail: runtimeConfig.supportEmail,
    appName: runtimeConfig.appName,
  },
  auth: {
    me: currentUser,
    isAuthenticated: async () => Boolean((await currentUser())?.email),
    signIn,
    signInWithProvider,
    refreshSession,
    resolveDashboardRedirect,
    oauthProviders: supabase && runtimeConfig.googleAuthEnabled ? ["google"] : [],
    redirectToLogin,
    logout,
    subscribe: subscribeAuth,
  },
  entities: Object.fromEntries(
    Object.keys(ENTITY_TABLES).map((entityName) => [entityName, createEntityApi(entityName)])
  ),
  functions: {
    async invoke(name, payload = {}) {
      const handler = functionHandlers[name];
      if (!handler) {
        throw new Error(`Unsupported function: ${name}`);
      }
      return handler(payload);
    },
  },
  integrations: {
    Core: {
      UploadFile: uploadFile,
    },
  },
};
